import {
  assetListResponseSchema,
  assetViewSchema,
  canvasViewV3Schema as canvasViewSchema,
  promptTemplateListResponseSchema,
  promptTemplateViewSchema,
  seedanceAdminGenerationHistoryResponseSchema,
  seedanceGenerationCreateResponseSchema,
  seedanceGenerationHistoryResponseSchema,
  auditEventListResponseSchema,
  seedanceCapabilitiesViewSchema,
  sharedImportResponseSchema,
  sharedLibraryItemViewSchema,
  sharedLibraryListResponseSchema,
  workflowInsertionViewV3Schema as workflowInsertionViewSchema,
  canvasNodeV3Schema,
  canvasEdgeSchema,
  canvasGroupSchema,
  pointSchema,
  type CanvasClipboardV3,
  type AssetListQuery,
  type AssetListResponse,
  type AssetView,
  type CanvasViewV3 as CanvasView,
  type CanvasDocumentV3 as CanvasDocument,
  type CanvasEdge,
  type CanvasGroup,
  type CanvasNodeV3 as CanvasNode,
  type CreatePromptTemplateRequest,
  type Point,
  type PromptTemplateView,
  type SeedanceAdminGenerationHistoryResponse,
  type SeedanceGenerationJobView,
  type SeedanceGenerationHistoryResponse,
  type SeedanceGenerationState,
  type AuditEventListResponse,
  type PublishAssetRequest,
  type PublishPromptRequest,
  type SaveCanvasV3Request as SaveCanvasRequest,
  type SeedanceCapabilitiesView,
  type SharedImportResponse,
  type SharedLibraryItemView,
  type SharedLibraryListQuery,
  type SharedLibraryListResponse,
  type UpdatePromptTemplateRequest,
  type WorkflowInsertionViewV3 as WorkflowInsertionView
} from "@seedance/contracts";
import { z } from "zod";

const workflowViewV3Schema = z.strictObject({
  id: z.uuid(), name: z.string().min(1).max(160), documentSchemaVersion: z.literal(3),
  nodes: z.array(canvasNodeV3Schema).min(1).max(5_000),
  edges: z.array(canvasEdgeSchema).max(10_000), groups: z.array(canvasGroupSchema).max(1_000),
  relativePositions: z.record(z.uuid(),pointSchema),
  createdAt: z.iso.datetime(), updatedAt: z.iso.datetime()
});

export type UserRole = "member" | "system_admin";

export interface UserSummary {
  id: string;
  username: string;
  role: UserRole;
  status: "active" | "disabled";
  mustChangePassword: boolean;
}

export interface SessionView {
  user: UserSummary;
  csrfToken: string;
}

export interface ProjectFolderView {
  id: string;
  ownerId: string;
  name: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectView {
  id: string;
  ownerId: string;
  folderId: string | null;
  canvasId: string;
  name: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export type WorkflowView = z.infer<typeof workflowViewV3Schema>;

export type WorkflowClipboard = CanvasClipboardV3;

export interface GenerationHistoryQuery {
  state?: SeedanceGenerationState;
  model?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit: number;
}

export interface AdministratorGenerationHistoryQuery extends GenerationHistoryQuery {
  ownerId?: string;
}

export interface AdministratorAuditQuery {
  targetType?: string;
  ownerId?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit: number;
}

interface ApiErrorBody {
  error?: {
    code?: unknown;
    message?: unknown;
    requestId?: unknown;
  };
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string;

  constructor(input: {
    status: number;
    code: string;
    message: string;
    requestId: string;
  }) {
    super(input.message);
    this.name = "ApiClientError";
    this.status = input.status;
    this.code = input.code;
    this.requestId = input.requestId;
  }
}

// Preserve the existing TypeError offline checks while distinguishing failed
// fetches from unrelated TypeErrors in response handling or application code.
export class ApiNetworkError extends TypeError {
  constructor(cause: TypeError) {
    // Shared callers keep the original diagnostic text; login renders only
    // its fixed, safe message instead of exposing transport details.
    super(cause.message);
    this.name = "ApiNetworkError";
  }
}

export function isAuthenticationRequired(
  error: unknown
): error is ApiClientError {
  return (
    error instanceof ApiClientError &&
    error.status === 401 &&
    error.code === "AUTHENTICATION_REQUIRED"
  );
}

export interface ApiClient {
  requestJson<T>(path: string, init?: RequestInit & { csrf?: boolean }): Promise<T>;
  requestText(path: string, init?: RequestInit & { csrf?: boolean }): Promise<string>;
  requestBlob(path: string, init?: RequestInit & { csrf?: boolean }): Promise<Blob>;
  setCsrfToken(token: string | undefined): void;
  getCsrfToken(): string | undefined;
  login(input: { username: string; password: string }): Promise<SessionView>;
  getSession(): Promise<SessionView>;
  changePassword(input: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void>;
  logout(): Promise<void>;
  listFolders(): Promise<ProjectFolderView[]>;
  listProjects(): Promise<ProjectView[]>;
  getProject(projectId: string): Promise<ProjectView>;
  createProject(input: {
    name: string;
    folderId?: string | null;
  }): Promise<ProjectView>;
  getCanvas(projectId: string): Promise<CanvasView>;
  saveCanvas(projectId: string, input: SaveCanvasRequest): Promise<CanvasView>;
  createWorkflow(input: { name: string; clipboard: WorkflowClipboard }): Promise<WorkflowView>;
  listWorkflows(): Promise<WorkflowView[]>;
  insertWorkflow(input: {
    workflowId: string;
    projectId: string;
    revision: string;
    anchor: Point;
  }): Promise<WorkflowInsertionView>;
  listAssets(query: AssetListQuery): Promise<AssetListResponse>;
  getAsset(assetId: string): Promise<AssetView>;
  trashAsset(assetId: string): Promise<AssetView>;
  restoreAsset(assetId: string): Promise<AssetView>;
  touchAsset(assetId: string): Promise<AssetView>;
  unpublishTeamAsset(assetId: string): Promise<AssetView>;
  copyTeamAssetToMine(assetId: string): Promise<AssetView>;
  listPromptTemplates(view: "active" | "trash"): Promise<PromptTemplateView[]>;
  createPromptTemplate(input: CreatePromptTemplateRequest): Promise<PromptTemplateView>;
  updatePromptTemplate(
    templateId: string,
    input: UpdatePromptTemplateRequest
  ): Promise<PromptTemplateView>;
  trashPromptTemplate(templateId: string): Promise<PromptTemplateView>;
  restorePromptTemplate(templateId: string): Promise<PromptTemplateView>;
  listSharedLibrary(query: SharedLibraryListQuery): Promise<SharedLibraryListResponse>;
  publishSharedAsset(input: PublishAssetRequest): Promise<SharedLibraryItemView>;
  publishSharedPrompt(input: PublishPromptRequest): Promise<SharedLibraryItemView>;
  withdrawSharedItem(itemId: string): Promise<SharedLibraryItemView>;
  importSharedItem(itemId: string): Promise<SharedImportResponse>;
  getSeedanceCapabilities(): Promise<SeedanceCapabilitiesView>;
  createGeneration(input: {
    projectId: string;
    canvasRevision: string;
    targetNodeId: string;
    commandId: string;
  }): Promise<SeedanceGenerationJobView>;
  insertGenerationOutput(input: {
    generationId: string;
    outputAssetId: string;
    canvasRevision: string;
  }): Promise<CanvasView>;
  listMyGenerationHistory(query: GenerationHistoryQuery): Promise<SeedanceGenerationHistoryResponse>;
  listAdminGenerationHistory(query: AdministratorGenerationHistoryQuery): Promise<SeedanceAdminGenerationHistoryResponse>;
  getAdminGeneration(jobId: string): Promise<SeedanceGenerationJobView>;
  listAdminAuditEvents(query: AdministratorAuditQuery): Promise<AuditEventListResponse>;
  listAdminTrashedAssets(query: Omit<AssetListQuery, "view">): Promise<AssetListResponse>;
  restoreAssetAsAdministrator(assetId: string): Promise<AssetView>;
}

export function createApiClient(options: {
  fetch?: typeof globalThis.fetch;
} = {}): ApiClient {
  const fetchImplementation =
    options.fetch ?? globalThis.fetch.bind(globalThis);
  let csrfToken: string | undefined;

  async function request<T>(
    path: string,
    init: RequestInit & { csrf?: boolean } = {},
    format: "json" | "text" | "blob" = "json"
  ): Promise<T> {
    const { csrf, ...requestInit } = init;
    const headers: Record<string, string> = {};
    new Headers(init.headers).forEach((value, key) => {
      headers[key] = value;
    });
    headers.Accept = format === "json" ? "application/json" : "*/*";
    if (init.body !== undefined && !new Headers(init.headers).has("Content-Type")) {
      headers["Content-Type"] = "application/json";
    }
    if (csrf && csrfToken !== undefined) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    let response: Response;
    try {
      response = await fetchImplementation(path, {
        ...requestInit,
        credentials: "same-origin",
        headers
      });
    } catch (cause) {
      if (cause instanceof TypeError) throw new ApiNetworkError(cause);
      throw cause;
    }
    if (response.ok) {
      if (response.status === 204) return undefined as T;
      return (await (format === "text" ? response.text() : format === "blob" ? response.blob() : response.json())) as T;
    }

    const body = (await response
      .json()
      .catch(() => ({}))) as ApiErrorBody;
    const code =
      typeof body.error?.code === "string"
        ? body.error.code
        : "REQUEST_FAILED";
    if (response.status === 401 && code === "AUTHENTICATION_REQUIRED") {
      csrfToken = undefined;
    }
    throw new ApiClientError({
      status: response.status,
      code,
      message:
        typeof body.error?.message === "string"
          ? body.error.message
          : "请求未能完成。",
      requestId:
        typeof body.error?.requestId === "string"
          ? body.error.requestId
          : "unavailable"
    });
  }

  function acceptSession(session: SessionView): SessionView {
    csrfToken = session.csrfToken;
    return session;
  }

  function generationQuery(query: GenerationHistoryQuery, ownerId?: string): string {
    const search = new URLSearchParams();
    if (ownerId) search.set("ownerId", ownerId);
    if (query.state) search.set("state", query.state);
    if (query.model) search.set("model", query.model);
    if (query.from) search.set("from", query.from);
    if (query.to) search.set("to", query.to);
    if (query.cursor) search.set("cursor", query.cursor);
    search.set("limit", String(query.limit));
    return search.toString();
  }

  return {
    requestJson: request,
    requestText: (path, init) => request<string>(path, init, "text"),
    requestBlob: (path, init) => request<Blob>(path, init, "blob"),
    setCsrfToken(token) {
      csrfToken = token;
    },
    getCsrfToken() {
      return csrfToken;
    },
    async login(input) {
      return acceptSession(
        await request<SessionView>("/api/auth/login", {
          method: "POST",
          body: JSON.stringify(input)
        })
      );
    },
    async getSession() {
      return acceptSession(await request<SessionView>("/api/auth/session"));
    },
    async changePassword(input) {
      await request<void>("/api/auth/change-password", {
        method: "POST",
        body: JSON.stringify(input),
        csrf: true
      });
      csrfToken = undefined;
    },
    async logout() {
      try {
        await request<void>("/api/auth/logout", {
          method: "POST",
          csrf: true
        });
      } catch (error) {
        if (
          !(
            error instanceof ApiClientError &&
            error.status === 403 &&
            error.code === "INVALID_CSRF_TOKEN"
          )
        ) {
          throw error;
        }
        acceptSession(await request<SessionView>("/api/auth/session"));
        await request<void>("/api/auth/logout", {
          method: "POST",
          csrf: true
        });
      }
      csrfToken = undefined;
    },
    listFolders() {
      return request<ProjectFolderView[]>("/api/project-folders");
    },
    listProjects() {
      return request<ProjectView[]>("/api/projects");
    },
    getProject(projectId) {
      return request<ProjectView>(
        `/api/projects/${encodeURIComponent(projectId)}`
      );
    },
    createProject(input) {
      return request<ProjectView>("/api/projects", {
        method: "POST",
        body: JSON.stringify({
          name: input.name,
          folderId: input.folderId ?? null
        }),
        csrf: true
      });
    },
    async getCanvas(projectId) {
      return canvasViewSchema.parse(
        await request<unknown>(
          `/api/projects/${encodeURIComponent(projectId)}/canvas`, { headers: { "X-Seedance-Canvas-Schema": "3" } }
        )
      );
    },
    async saveCanvas(projectId, input) {
      return canvasViewSchema.parse(
        await request<unknown>(
          `/api/projects/${encodeURIComponent(projectId)}/canvas`,
          {
            method: "PUT",
            headers: { "X-Seedance-Canvas-Schema": "3" },
            body: JSON.stringify(input),
            csrf: true
          }
        )
      );
    },
    async createWorkflow(input) {
      return workflowViewV3Schema.parse(await request<unknown>("/api/workflows", {
        method: "POST", headers: { "X-Seedance-Canvas-Schema": "3" },
        body: JSON.stringify(input), csrf: true
      }));
    },
    async listWorkflows() {
      const response = await request<unknown>("/api/workflows", {headers: { "X-Seedance-Canvas-Schema": "3" }});
      return z.array(workflowViewV3Schema).parse(response);
    },
    async insertWorkflow(input) {
      return workflowInsertionViewSchema.parse(await request<unknown>(
        `/api/workflows/${encodeURIComponent(input.workflowId)}/insert`,
        {
          method: "POST",
          headers: { "X-Seedance-Canvas-Schema": "3" },
          body: JSON.stringify({
            projectId: input.projectId,
            revision: input.revision,
            anchor: input.anchor
          }),
          csrf: true
        }
      ));
    },
    async listAssets(query) {
      const search = new URLSearchParams({ view: query.view });
      if (query.kind) search.set("kind", query.kind);
      if (query.q) search.set("q", query.q);
      if (query.cursor) search.set("cursor", query.cursor);
      search.set("limit", String(query.limit));
      return assetListResponseSchema.parse(
        await request<unknown>(`/api/assets?${search.toString()}`)
      );
    },
    async getAsset(assetId) {
      return assetViewSchema.parse(
        await request<unknown>(`/api/assets/${encodeURIComponent(assetId)}`)
      );
    },
    async trashAsset(assetId) {
      return assetViewSchema.parse(
        await request<unknown>(`/api/assets/${encodeURIComponent(assetId)}`, {
          method: "DELETE",
          csrf: true
        })
      );
    },
    async restoreAsset(assetId) {
      return assetViewSchema.parse(
        await request<unknown>(
          `/api/assets/${encodeURIComponent(assetId)}/restore`,
          { method: "POST", csrf: true }
        )
      );
    },
    async touchAsset(assetId) {
      return assetViewSchema.parse(
        await request<unknown>(
          `/api/assets/${encodeURIComponent(assetId)}/touch`,
          { method: "POST", csrf: true }
        )
      );
    },
    async unpublishTeamAsset(assetId) {
      return assetViewSchema.parse(
        await request<unknown>(
          `/api/assets/${encodeURIComponent(assetId)}/unpublish`,
          { method: "POST", csrf: true }
        )
      );
    },
    async copyTeamAssetToMine(assetId) {
      return assetViewSchema.parse(
        await request<unknown>(
          `/api/assets/${encodeURIComponent(assetId)}/copy-to-mine`,
          { method: "POST", csrf: true }
        )
      );
    },
    async listPromptTemplates(view) {
      const response = promptTemplateListResponseSchema.parse(
        await request<unknown>(
          `/api/prompt-templates?${new URLSearchParams({ view }).toString()}`
        )
      );
      return response.templates;
    },
    async createPromptTemplate(input) {
      return promptTemplateViewSchema.parse(
        await request<unknown>("/api/prompt-templates", {
          method: "POST",
          body: JSON.stringify(input),
          csrf: true
        })
      );
    },
    async updatePromptTemplate(templateId, input) {
      return promptTemplateViewSchema.parse(
        await request<unknown>(
          `/api/prompt-templates/${encodeURIComponent(templateId)}`,
          { method: "PATCH", body: JSON.stringify(input), csrf: true }
        )
      );
    },
    async trashPromptTemplate(templateId) {
      return promptTemplateViewSchema.parse(
        await request<unknown>(
          `/api/prompt-templates/${encodeURIComponent(templateId)}`,
          { method: "DELETE", csrf: true }
        )
      );
    },
    async restorePromptTemplate(templateId) {
      return promptTemplateViewSchema.parse(
        await request<unknown>(
          `/api/prompt-templates/${encodeURIComponent(templateId)}/restore`,
          { method: "POST", csrf: true }
        )
      );
    },
    async listSharedLibrary(query) {
      const search = new URLSearchParams();
      if (query.kind) search.set("kind", query.kind);
      if (query.q) search.set("q", query.q);
      if (query.cursor) search.set("cursor", query.cursor);
      search.set("limit", String(query.limit));
      return sharedLibraryListResponseSchema.parse(
        await request<unknown>(`/api/shared-library?${search.toString()}`)
      );
    },
    async publishSharedAsset(input) {
      return sharedLibraryItemViewSchema.parse(
        await request<unknown>("/api/shared-library/assets", {
          method: "POST",
          body: JSON.stringify(input),
          csrf: true
        })
      );
    },
    async publishSharedPrompt(input) {
      return sharedLibraryItemViewSchema.parse(
        await request<unknown>("/api/shared-library/prompts", {
          method: "POST",
          body: JSON.stringify(input),
          csrf: true
        })
      );
    },
    async withdrawSharedItem(itemId) {
      return sharedLibraryItemViewSchema.parse(
        await request<unknown>(
          `/api/shared-library/${encodeURIComponent(itemId)}`,
          { method: "DELETE", csrf: true }
        )
      );
    },
    async importSharedItem(itemId) {
      return sharedImportResponseSchema.parse(
        await request<unknown>(
          `/api/shared-library/${encodeURIComponent(itemId)}/import`,
          { method: "POST", csrf: true }
        )
      );
    },
    async getSeedanceCapabilities() {
      return seedanceCapabilitiesViewSchema.parse(
        await request<unknown>("/api/seedance/capabilities")
      );
    },
    async createGeneration(input) {
      const response = seedanceGenerationCreateResponseSchema.parse(
        await request<unknown>(
          `/api/projects/${encodeURIComponent(input.projectId)}/generations`,
          {
            method: "POST",
            body: JSON.stringify({
              canvasRevision: input.canvasRevision,
              targetNodeId: input.targetNodeId,
              commandId: input.commandId
            }),
            csrf: true
          }
        )
      );
      return response.job;
    },
    async insertGenerationOutput(input) {
      return canvasViewSchema.parse(await request<unknown>(
        `/api/generations/${encodeURIComponent(input.generationId)}/outputs/${encodeURIComponent(input.outputAssetId)}/insert`,
        {
          method: "POST",
          headers: { "X-Seedance-Canvas-Schema": "3" },
          body: JSON.stringify({ canvasRevision: input.canvasRevision }),
          csrf: true
        }
      ));
    },
    async listMyGenerationHistory(query) {
      return seedanceGenerationHistoryResponseSchema.parse(
        await request<unknown>(`/api/generations/mine?${generationQuery(query)}`)
      );
    },
    async listAdminGenerationHistory(query) {
      return seedanceAdminGenerationHistoryResponseSchema.parse(
        await request<unknown>(
          `/api/admin/generations?${generationQuery(query, query.ownerId)}`
        )
      );
    },
    async getAdminGeneration(jobId) {
      const response = seedanceGenerationCreateResponseSchema.parse(
        await request<unknown>(`/api/admin/generations/${encodeURIComponent(jobId)}`)
      );
      return response.job;
    },
    async listAdminAuditEvents(query) {
      const search = new URLSearchParams();
      if (query.targetType) search.set("targetType", query.targetType);
      if (query.ownerId) search.set("ownerId", query.ownerId);
      if (query.from) search.set("from", query.from);
      if (query.to) search.set("to", query.to);
      if (query.cursor) search.set("cursor", query.cursor);
      search.set("limit", String(query.limit));
      return auditEventListResponseSchema.parse(
        await request<unknown>(`/api/admin/audit-events?${search.toString()}`)
      );
    },
    async listAdminTrashedAssets(query) {
      const search = new URLSearchParams();
      if (query.kind) search.set("kind", query.kind);
      if (query.q) search.set("q", query.q);
      if (query.cursor) search.set("cursor", query.cursor);
      search.set("limit", String(query.limit));
      return assetListResponseSchema.parse(
        await request<unknown>(`/api/admin/assets?${search.toString()}`)
      );
    },
    async restoreAssetAsAdministrator(assetId) {
      return assetViewSchema.parse(
        await request<unknown>(
          `/api/admin/assets/${encodeURIComponent(assetId)}/restore`,
          { method: "POST", csrf: true }
        )
      );
    }
  };
}

export const apiClient = createApiClient();
